import { Link } from 'react-router-dom';
import {
  BookOpen,
  Bot,
  Building2,
  Bus as BusIcon,
  Camera,
  CircleHelp,
  ClipboardCheck,
  ClipboardList,
  FileStack,
  FolderArchive,
  CalendarCheck,
  CalendarDays,
  FileText,
  Images,
  LineChart,
  LogOut,
  Megaphone,
  MessageCircle,
  MessageSquareHeart,
  MessagesSquare,
  Settings as SettingsIcon,
  Pill,
  ReceiptText,
  Syringe,
  School,
  Search as SearchIcon,
  ShieldCheck,
  UserPlus,
  UtensilsCrossed,
  NotebookPen,
  NotebookText,
  Sprout,
} from 'lucide-react';
import {
  BRAND,
  DEVELOPMENT_COPY,
  ROLES,
  ROLE_LABELS,
  SUBSCRIPTION_COPY,
  SUBSCRIPTION_STATUS_LABELS,
} from '@saesak/shared';
import type { SubscriptionStatus } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Avatar, Badge, Button, Card, Chip } from '@/components/ui';
import { useAuth } from '@/store/auth';

/** 전체 메뉴 — 하단 탭에 담기지 않은 화면들의 입구 (§7.5 v1.8) */
export function MorePage() {
  const user = useAuth((s) => s.user);
  const membership = useAuth((s) => s.membership);
  const switchFacility = useAuth((s) => s.switchFacility);
  const logout = useAuth((s) => s.logout);

  if (!user || !membership) return null;

  const isParent = membership.role === ROLES.PARENT;
  const isDirector = membership.role === ROLES.DIRECTOR;

  return (
    <Screen>
      <AppBar title="전체" />

      <div className="flex flex-col gap-4 p-4">
        <Card className="flex items-center gap-3">
          <Avatar name={user.name} size={52} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-bold">{user.name}</p>
            <p className="truncate text-sm text-ink-sub">{user.email}</p>
          </div>
          <Chip tone="primary">{ROLE_LABELS[membership.role as keyof typeof ROLE_LABELS]}</Chip>
        </Card>

        {/* 여러 기관에 속한 경우 전환 (§4 멀티테넌시 — 보호자 다자녀·다기관) */}
        {user.memberships.length > 1 && (
          <Card>
            <p className="mb-2 text-sm font-semibold text-ink">기관 전환</p>
            <div className="flex flex-wrap gap-2">
              {user.memberships.map((item) => (
                <Chip
                  key={item.id}
                  tone={item.facilityId === membership.facilityId ? 'primary' : 'neutral'}
                  selected={item.facilityId === membership.facilityId}
                  onClick={() => switchFacility(item.facilityId)}
                >
                  {item.facilityName}
                </Chip>
              ))}
            </div>
          </Card>
        )}

        {!membership.canWrite && (
          <Card className="bg-accent-soft">
            <p className="text-base">
              {membership.subscriptionStatus === 'GRACE'
                ? SUBSCRIPTION_COPY.graceBanner
                : SUBSCRIPTION_COPY.expiredBanner}
            </p>
          </Card>
        )}

        {/*
         * 주제별 3열 아이콘 타일 — 메뉴가 많아 쓰임새(주제) 단위로 묶는다.
         * §7.1의 "아이콘 그리드 금지"는 홈 화면 가드다. 홈은 카드 피드를 지키고,
         * 전체 메뉴는 기능 서랍이므로 한눈에 훑는 그리드가 맞다.
         */}
        {isParent ? (
          <>
            <MenuGroup label="아이의 하루">
              <MenuTile to="/notes" icon={<FileText size={22} />} label="알림장" />
              <MenuTile to="/album" icon={<Images size={22} />} label="앨범" />
              <MenuTile to="/notices" icon={<Megaphone size={22} />} label="공지사항" />
              <MenuTile to="/schedule" icon={<CalendarDays size={22} />} label="일정표" />
              <MenuTile to="/meals" icon={<UtensilsCrossed size={22} />} label="식단표" />
              <MenuTile to="/bus" icon={<BusIcon size={22} />} label="버스" />
              <MenuTile to="/about" icon={<School size={22} />} label="원 소개" />
            </MenuGroup>

            <MenuGroup label="성장 기록">
              <MenuTile to="/scope" icon={<SearchIcon size={22} />} label={DEVELOPMENT_COPY.tabName} />
              <MenuTile to="/growth" icon={<LineChart size={22} />} label="신체 성장" />
              <MenuTile to="/health-records" icon={<Syringe size={22} />} label="예방접종·병력" />
              <MenuTile to="/life-records" icon={<FileText size={22} />} label="생활기록부" />
              <MenuTile to="/curriculum" icon={<BookOpen size={22} />} label="교육계획안" />
            </MenuGroup>

            <MenuGroup label="요청·제출">
              <MenuTile to="/medications" icon={<Pill size={22} />} label="투약의뢰서" />
              <MenuTile to="/pickups" icon={<ShieldCheck size={22} />} label="귀가동의서" />
              <MenuTile to="/consents" icon={<ClipboardCheck size={22} />} label="동의서" />
              <MenuTile to="/renewals" icon={<FileText size={22} />} label="서류 제출" />
              <MenuTile to="/counseling" icon={<MessagesSquare size={22} />} label="부모면담" />
              <MenuTile to="/chat" icon={<Bot size={22} />} label="육아상담" />
            </MenuGroup>

            <MenuGroup label="이용 안내">
              <MenuTile to="/settings" icon={<SettingsIcon size={22} />} label="앱 설정" />
              <MenuTile to="/help" icon={<CircleHelp size={22} />} label="도움말" />
              <MenuTile to="/settings/feedback" icon={<MessageSquareHeart size={22} />} label="불편·제안" />
            </MenuGroup>
          </>
        ) : (
          <>
            <MenuGroup label="우리 반 기록">
              <MenuTile to="/notes" icon={<FileText size={22} />} label="알림장" />
              <MenuTile to="/attendance" icon={<CalendarCheck size={22} />} label="출석부" />
              <MenuTile to="/development-tasks" icon={<Sprout size={22} />} label="발달기록" />
              <MenuTile to="/observations" icon={<NotebookPen size={22} />} label="관찰메모" />
              <MenuTile to="/album" icon={<Images size={22} />} label="앨범" />
              <MenuTile to="/life-records" icon={<FileText size={22} />} label="생활기록부" />
              <MenuTile to="/growth" icon={<LineChart size={22} />} label="신체 성장" />
              <MenuTile to="/health-records" icon={<Syringe size={22} />} label="예방접종·병력" />
              <MenuTile to="/handover" icon={<ClipboardList size={22} />} label="인수인계" />
            </MenuGroup>

            <MenuGroup label="가정과 소통">
              <MenuTile to="/notices" icon={<Megaphone size={22} />} label="공지사항" />
              <MenuTile to="/records/notices/new" icon={<Megaphone size={22} />} label="공지 쓰기" />
              <MenuTile to="/talk" icon={<MessageCircle size={22} />} label="가정 소식" />
              <MenuTile to="/counseling" icon={<MessagesSquare size={22} />} label="부모면담" />
              <MenuTile to="/consents" icon={<ClipboardCheck size={22} />} label="동의서" />
              <MenuTile to="/medications" icon={<Pill size={22} />} label="투약의뢰서" />
              <MenuTile to="/pickups" icon={<ShieldCheck size={22} />} label="귀가동의서" />
            </MenuGroup>

            <MenuGroup label="일정·식단">
              <MenuTile to="/schedule" icon={<CalendarDays size={22} />} label="일정표" />
              <MenuTile to="/records/events/new" icon={<CalendarDays size={22} />} label="일정 등록" />
              <MenuTile to="/meals" icon={<UtensilsCrossed size={22} />} label="식단표" />
              <MenuTile to="/records/meals/edit" icon={<UtensilsCrossed size={22} />} label="식단 작성" />
              <MenuTile to="/curriculum" icon={<BookOpen size={22} />} label="교육계획" />
              <MenuTile to="/care-journal" icon={<NotebookText size={22} />} label="보육일지" />
              <MenuTile to="/photo-guide" icon={<Camera size={22} />} label="촬영 가이드" />
            </MenuGroup>

            <MenuGroup label="원 운영">
              <MenuTile to="/bus" icon={<BusIcon size={22} />} label="버스 운행" />
              {isDirector && <MenuTile to="/bus/manage" icon={<BusIcon size={22} />} label="버스 관리" />}
              <MenuTile to="/documents" icon={<FolderArchive size={22} />} label="전자 서류함" />
              <MenuTile to="/forms" icon={<FileStack size={22} />} label="양식 자료실" />
              <MenuTile to="/ops" icon={<ClipboardList size={22} />} label="운영 도구" />
              <MenuTile to="/purchases" icon={<ReceiptText size={22} />} label="구매대장" />
              <MenuTile to="/about" icon={<School size={22} />} label="원 소개" />
              {isDirector && <MenuTile to="/waitlist" icon={<UserPlus size={22} />} label="입소 대기" />}
              {isDirector && <MenuTile to="/operations" icon={<Building2 size={22} />} label="앱 관리" />}
            </MenuGroup>

            <MenuGroup label="이용 안내">
              <MenuTile to="/settings" icon={<SettingsIcon size={22} />} label="앱 설정" />
              <MenuTile to="/help" icon={<CircleHelp size={22} />} label="도움말" />
              <MenuTile to="/settings/feedback" icon={<MessageSquareHeart size={22} />} label="불편·제안" />
            </MenuGroup>
          </>
        )}

        <Card>
          <p className="text-sm font-semibold text-ink">구독 상태</p>
          <p className="mt-1 text-base">
            {
              SUBSCRIPTION_STATUS_LABELS[
                membership.subscriptionStatus as SubscriptionStatus
              ]
            }
          </p>
        </Card>

        <Button variant="outline" block icon={<LogOut size={18} />} onClick={() => void logout()}>
          로그아웃
        </Button>

        <div className="flex flex-col items-center gap-1.5 pb-4">
          <img src="/brand-icon.svg" alt="" width={40} height={40} />
          <p className="text-center text-sm text-ink-sub">
            {BRAND.name} · {BRAND.tagline}
          </p>
        </div>
      </div>
    </Screen>
  );
}

/** 주제 묶음 — 작은 라벨 + 3열 타일 그리드 */
function MenuGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      <p className="px-1 pb-1.5 text-sm font-semibold text-ink-sub">{label}</p>
      <nav className="grid grid-cols-3 gap-2">{children}</nav>
    </section>
  );
}

function MenuTile({
  to,
  icon,
  label,
  badge,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  badge?: number;
}) {
  return (
    <Link
      to={to}
      className="tap relative flex flex-col items-center gap-1.5 rounded-md bg-surface px-1 py-3.5 shadow-card transition-colors hover:bg-primary-soft"
    >
      <span className="grid h-11 w-11 place-items-center rounded-full bg-primary-soft text-primary-strong">
        {icon}
      </span>
      <span className="text-center text-sm font-medium leading-tight">{label}</span>
      {badge ? (
        <span className="absolute right-2 top-2">
          <Badge count={badge} />
        </span>
      ) : null}
    </Link>
  );
}
